import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Close, ChevronDown } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core/i18n';
import type { TagsInputProps, TagOption } from './TagsInput.types';
import {
  Container,
  InputContainer,
  Tag,
  TagLabel,
  RemoveButton,
  InputWrapper,
  StyledInput,
  ChevronWrapper,
  Dropdown,
  DropdownList,
  OptionButton,
  EmptyState,
} from './TagsInput.styles';

export const TagsInput: React.FC<TagsInputProps> = ({ options, value = [], onChange, placeholder, disabled = false, 'data-testid': dataTestId }) => {
  const { tq } = useTranslation('generalEventSchedule');
  const defaultPlaceholder = placeholder ?? tq`Add a label...`;
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);

  // Reset highlighted index when filtered options change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [inputValue]);

  // Scroll highlighted option into view
  useEffect(() => {
    const dropdown = optionsRef.current;
    if (dropdown && isFocused) {
      const highlightedOption = dropdown.querySelector(`[data-highlighted="true"]`) as HTMLElement;
      if (highlightedOption) {
        highlightedOption.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex, isFocused]);

  const handleContainerClick = useCallback(() => {
    if (!disabled && inputRef.current) {
      inputRef.current.focus();
    }
  }, [disabled]);

  const handleInputFocus = useCallback(() => {
    if (!disabled) {
      setIsFocused(true);
    }
  }, [disabled]);

  const handleInputBlur = useCallback(() => {
    setTimeout(() => {
      setIsFocused(false);
      setInputValue('');
    }, 150);
  }, []);

  // Memoize selected IDs as a Set for O(1) lookup
  const selectedIds = useMemo(() => new Set(value.map(v => v.id)), [value]);

  // Memoize available options (options not already selected)
  const availableOptions = useMemo(() => options.filter(option => !selectedIds.has(option.id)), [options, selectedIds]);

  // Memoize filtered options based on input value
  const filteredOptions = useMemo(() => {
    if (!inputValue) return availableOptions;
    const searchTerm = inputValue.toLowerCase();
    return availableOptions.filter(option => option.label.toLowerCase().includes(searchTerm));
  }, [availableOptions, inputValue]);

  const handleAdd = useCallback(
    (option: TagOption) => {
      if (disabled || (option.id !== '' && selectedIds.has(option.id))) return;

      const newValue = [...value, option];
      onChange?.(newValue);
      setInputValue('');

      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    },
    [disabled, selectedIds, value, onChange],
  );

  const getSelectableOptions = useCallback(() => {
    return filteredOptions.map(option => ({ type: 'option' as const, value: option }));
  }, [filteredOptions]);

  const handleRemove = useCallback(
    (tagId: string) => {
      if (disabled) return;
      const newValue = value.filter(v => v.id !== tagId);
      onChange?.(newValue);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    },
    [disabled, value, onChange],
  );

  const showDropdown = isFocused && filteredOptions.length > 0;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const selectableOptions = getSelectableOptions();

      switch (e.key) {
        case 'Escape':
          inputRef.current?.blur();
          break;

        case 'Backspace':
          if (inputValue === '' && value.length > 0) {
            const newValue = [...value];
            newValue.pop();
            onChange?.(newValue);
          }
          break;

        case 'ArrowDown':
          if (showDropdown) {
            e.preventDefault();
            setHighlightedIndex(prev => (prev < selectableOptions.length - 1 ? prev + 1 : 0));
          }
          break;

        case 'ArrowUp':
          if (showDropdown) {
            e.preventDefault();
            setHighlightedIndex(prev => (prev > 0 ? prev - 1 : selectableOptions.length - 1));
          }
          break;

        case 'Enter':
          if (showDropdown && selectableOptions.length > 0) {
            e.preventDefault();
            const selected = selectableOptions[highlightedIndex];
            if (selected) {
              handleAdd(selected.value);
            }
          }
          break;
      }
    },
    [getSelectableOptions, inputValue, value, onChange, highlightedIndex, handleAdd, showDropdown],
  );

  return (
    <Container data-testid={dataTestId}>
      <InputContainer
        ref={containerRef}
        onClick={handleContainerClick}
        $isFocused={isFocused}
        $isDisabled={disabled}
      >
        {value.map(tag => (
          <Tag key={tag.id}>
            <TagLabel>{tag.label}</TagLabel>
            <RemoveButton
              type="button"
              onMouseDown={e => {
                e.preventDefault();
                e.stopPropagation();
                handleRemove(tag.id);
              }}
              disabled={disabled}
              aria-label={tq`Remove ${tag.label}`}
            >
              <Close size={12} />
            </RemoveButton>
          </Tag>
        ))}
        <InputWrapper>
          <StyledInput
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            placeholder={value.length === 0 ? defaultPlaceholder : ''}
            autoComplete="off"
          />
          <ChevronWrapper $isOpen={isFocused}>
            <ChevronDown size={14} />
          </ChevronWrapper>
        </InputWrapper>
      </InputContainer>
      {showDropdown && (
        <Dropdown
          role="listbox"
          id="tags-listbox"
        >
          <DropdownList ref={optionsRef}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => {
                const isHighlighted = highlightedIndex === index;
                return (
                  <OptionButton
                    key={option.id}
                    type="button"
                    role="option"
                    aria-selected={isHighlighted}
                    data-highlighted={isHighlighted}
                    $isHighlighted={isHighlighted}
                    onMouseDown={e => {
                      e.preventDefault();
                      handleAdd(option);
                    }}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    {option.label}
                  </OptionButton>
                );
              })
            ) : (
              <EmptyState>{tq`No options available`}</EmptyState>
            )}
          </DropdownList>
        </Dropdown>
      )}
    </Container>
  );
};
